import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  CircleCheck,
  Flame,
  HeartCrack,
  HeartHandshake,
  Key,
  Loader2,
  Lock,
  PenLine,
  Plus,
  Sparkles,
  Trash2,
  Users,
  Wind,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LogoMark } from "@/components/logo";
import {
  ApiError,
  addStep4Row,
  getRelationships,
  getStep,
  getStep4Rows,
  startStep,
  updateStep,
  updateStep4Row,
  wipeStep4,
  type OutgoingShare,
  type Step4Pass,
  type Step4Row,
  type StepData,
} from "@/lib/api";
import { decryptRow, encryptRow } from "@/lib/step4-crypto";
import { shortDate } from "@/lib/format";

/**
 * Step 4 — the one step that breaks the reflective template (Step 4 PRD +
 * design canvas). A row-based inventory in three passes, written one row at a
 * time, END-TO-END ENCRYPTED on this device (src/lib/step4-crypto.ts): the
 * server only ever sees ciphertext. No sharing option, no AI, delete-at-will
 * (whole inventory only — never per row), progress kept after deletion.
 */

// Proposed labels — owner finalizes wording (PRD §3/§8.3). The structure is
// method; every word here is Happy24's own.
const PASSES: Record<
  Step4Pass,
  {
    label: string;
    one: string;
    lead: string;
    icon: typeof Flame;
    fields: { label: string; help: string; kind: "input" | "area" }[];
  }
> = {
  resentments: {
    label: "Resentments",
    one: "resentment",
    lead: "Who or what I resent",
    icon: Flame,
    fields: [
      { label: "Who or what", help: "A person, a place, an institution — one at a time.", kind: "input" },
      { label: "What happened", help: "The plain facts, as you remember them.", kind: "area" },
      { label: "What it affected in me", help: "Where it landed — what it touched or took.", kind: "area" },
      { label: "My part in it", help: "Written last, and only for you.", kind: "area" },
    ],
  },
  fears: {
    label: "Fears",
    one: "fear",
    lead: "What I'm afraid of",
    icon: Wind,
    fields: [
      { label: "What I'm afraid of", help: "Name it as small and specific as you can.", kind: "input" },
      { label: "What it threatens", help: "What you're afraid it would cost you.", kind: "area" },
      { label: "What I want to do about it", help: "However you understand that.", kind: "area" },
    ],
  },
  harms: {
    label: "Harms",
    one: "harm",
    lead: "Who was harmed",
    icon: HeartCrack,
    fields: [
      { label: "Who was harmed", help: "One person or one situation.", kind: "input" },
      { label: "What I did", help: "What happened, without softening it.", kind: "area" },
      { label: "My part", help: "Yours alone. Nobody else's side of it.", kind: "area" },
    ],
  },
};
const PASS_KEYS: Step4Pass[] = ["resentments", "fears", "harms"];

type Decrypted = Step4Row & { fields: string[] | null }; // null = other device's key

type Screen =
  | { s: "overview" }
  | { s: "hub" }
  | { s: "privacy" }
  | { s: "row"; pass: Step4Pass; rowId?: string }
  | { s: "list"; pass: Step4Pass }
  | { s: "review" }
  | { s: "ready" }
  | { s: "close" }
  | { s: "deleted" };

function EncryptedTag() {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Lock className="h-[13px] w-[13px]" strokeWidth={2} /> Encrypted · only you
    </span>
  );
}

function Note({ icon: Icon = Lock, children }: { icon?: typeof Lock; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
      <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
      <p className="text-[13px] leading-relaxed text-foreground">{children}</p>
    </div>
  );
}

export default function Step4() {
  const navigate = useNavigate();
  const [data, setData] = useState<StepData | null>(null);
  const [rows, setRows] = useState<Decrypted[]>([]);
  const [sponsors, setSponsors] = useState<OutgoingShare[]>([]);
  const [screen, setScreen] = useState<Screen>({ s: "overview" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  async function load() {
    try {
      const [d, r] = await Promise.all([getStep(4), getStep4Rows()]);
      setData(d);
      setRows(
        await Promise.all(
          r.rows.map(async (row) => {
            const dec = await decryptRow(row.ciphertext);
            return { ...row, fields: dec ? (JSON.parse(dec.v ?? "[]") as string[]) : null };
          })
        )
      );
      try {
        const rel = await getRelationships();
        setSponsors(rel.outgoing.filter((o) => o.role === "sponsor" && o.status === "active"));
      } catch {
        /* sponsor card just stays generic */
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError("Couldn't load Step 4.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byPass = useMemo(() => {
    const m: Record<Step4Pass, Decrypted[]> = { resentments: [], fears: [], harms: [] };
    for (const r of rows) m[r.pass]?.push(r);
    return m;
  }, [rows]);
  const total = rows.length;
  const foreignRows = rows.some((r) => r.fields === null);
  const lastPass = rows.length ? rows[rows.length - 1].pass : null;

  async function begin() {
    setBusy(true);
    try {
      if (!data?.work) await startStep(4);
      await updateStep(4, { current_phase: "inventory" });
      await load();
      setScreen({ s: "hub" });
    } catch {
      setError("Couldn't start Step 4.");
    } finally {
      setBusy(false);
    }
  }

  async function saveRow(pass: Step4Pass, rowId: string | undefined, fields: string[]) {
    setBusy(true);
    try {
      const ciphertext = await encryptRow({ v: JSON.stringify(fields) });
      if (rowId) {
        const updated = await updateStep4Row(rowId, ciphertext);
        setRows((rs) => rs.map((r) => (r.id === rowId ? { ...r, ...updated, fields } : r)));
      } else {
        const created = await addStep4Row(pass, ciphertext);
        setRows((rs) => [...rs, { ...created, fields }]);
      }
      setScreen({ s: "list", pass });
    } catch {
      setError("Couldn't save. Nothing you wrote left this screen — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function markReady() {
    setBusy(true);
    try {
      await updateStep(4, { status: "ready", ready_to_talk: true, current_phase: "close" });
      await load();
      setScreen({ s: "close" });
    } catch {
      setError("Couldn't mark Step 4 ready.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteEverything() {
    setBusy(true);
    try {
      await wipeStep4();
      setRows([]);
      setConfirmingDelete(false);
      setScreen({ s: "deleted" });
    } catch {
      setError("Couldn't delete. Your writing is unchanged.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => navigate("/steps")}>Back to Steps</Button>
      </div>
    );
  }
  if (!data) return null;
  if (!data.available) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <Lock className="h-8 w-8 text-muted-foreground" strokeWidth={2} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Step 4 isn't open yet</h1>
          <p className="mx-auto mt-2 max-w-[300px] text-sm leading-relaxed text-muted-foreground">
            It opens when you mark Step 3 ready. There's no rush to get there.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/steps")}>Back to Steps</Button>
      </div>
    );
  }

  const header = (title?: string, opts?: { back?: () => void; saved?: boolean }) => (
    <div className="flex items-center gap-2 px-4 pb-2 pt-6">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Back"
        onClick={opts?.back ?? (() => navigate("/steps"))}
      >
        <ArrowLeft className="h-5 w-5" strokeWidth={2} />
      </Button>
      <span className="flex-1 text-sm font-semibold text-foreground">{title}</span>
      {opts?.saved ? (
        <span className="pr-2 text-xs text-muted-foreground">Saved</span>
      ) : (
        <span className="pr-2"><EncryptedTag /></span>
      )}
    </div>
  );

  /* ------------------------------ overview ------------------------------ */
  if (screen.s === "overview") {
    const resumed = !!data.work && total > 0;
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header()}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Four{data.step.principle ? ` · ${data.step.principle}` : ""}
            </p>
            <h1 className="mt-1 text-2xl font-bold text-foreground">Step 4</h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">{data.step.intro_text}</p>
          {resumed ? (
            <>
              <Card>
                <CardContent className="flex flex-col gap-2.5 px-4 py-3.5">
                  <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Where you left off
                  </p>
                  {lastPass && (
                    <p className="flex items-center gap-2 text-sm text-foreground">
                      <PenLine className="h-3.5 w-3.5" strokeWidth={2} />
                      {PASSES[lastPass].label} — last written {shortDate(rows[rows.length - 1].updated_at)}
                    </p>
                  )}
                  <div className="flex gap-5 pt-0.5">
                    {PASS_KEYS.map((p) => (
                      <div key={p}>
                        <p className="text-lg font-bold text-foreground">{byPass[p].length}</p>
                        <p className="text-xs text-muted-foreground">{PASSES[p].label.toLowerCase()}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Nothing you've written has moved or changed. Pick it up exactly where you set it down.
              </p>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2.5">
                {PASS_KEYS.map((p) => {
                  const Icon = PASSES[p].icon;
                  return (
                    <div key={p} className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Icon className="h-4 w-4" strokeWidth={2} />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{PASSES[p].label}</p>
                        <p className="text-xs text-muted-foreground">{PASSES[p].lead}, one at a time</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Note>
                Everything you write here is encrypted on this device. Not your sponsor, not us —
                nobody else can read it. There's no AI anywhere in Step 4. You can delete all of it
                whenever you want, and keep your progress.
              </Note>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                This is written to be read to someone you trust. That's Step 5, and it's where it
                starts to lift.
              </p>
            </>
          )}
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth disabled={busy} onClick={begin}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : resumed ? (
                "Continue Where You Left Off"
              ) : data.work ? (
                "Continue Step 4"
              ) : (
                "Begin Step 4"
              )}
            </Button>
            <Button
              variant="ghost"
              fullWidth
              onClick={() => setScreen(resumed ? { s: "review" } : { s: "privacy" })}
            >
              {resumed ? "Review What You've Written" : "How This Is Stored"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------ privacy ------------------------------ */
  if (screen.s === "privacy") {
    const facts: [typeof Lock, string, string][] = [
      [Lock, "Encrypted on your device", "Your Step 4 writing is encrypted before it ever leaves this phone. Our servers hold only the scrambled version. We can't read it, and neither can anyone who works here."],
      [Users, "No sponsor access, at all", "Other steps let you share what you wrote. Step 4 doesn't have that option, on purpose. Step 5 is you reading it aloud, in person."],
      [Sparkles, "No AI touches it", "Nothing here is summarized, suggested, or reflected back to you. This writing is yours and your sponsor's alone."],
      [Trash2, "Yours to delete, anytime", "Most people delete it after Step 5. Your progress stays — you keep Step 4 marked done and move on to Step 5 and 6."],
    ];
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("How Step 4 is stored", { back: () => setScreen({ s: "overview" }) })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Storage · Step Four
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">Nobody can read this but you.</h1>
          </div>
          {facts.map(([Icon, t, d]) => (
            <div key={t} className="flex items-start gap-3">
              <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="h-[17px] w-[17px]" strokeWidth={2} />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground">{t}</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{d}</p>
              </div>
            </div>
          ))}
          <Note icon={Key}>
            Because only you hold the key, there's no way for us to recover this writing if you
            lose access to it. That's the trade for real privacy — and it's writing that's meant
            to be read once and then let go.
          </Note>
          <div className="mt-auto flex flex-col items-center gap-3 pt-4">
            <Button size="xl" fullWidth onClick={() => setScreen(data.work ? { s: "hub" } : { s: "overview" })}>
              Back to Step 4
            </Button>
            {total > 0 && (
              <button
                type="button"
                className="text-[13px] text-muted-foreground underline underline-offset-2"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete My Step 4 Writing
              </button>
            )}
          </div>
        </div>
        {confirmingDelete && deleteModal()}
      </div>
    );
  }

  /* -------------------------------- hub -------------------------------- */
  if (screen.s === "hub") {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 4", { back: () => setScreen({ s: "overview" }) })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-3 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Your inventory
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              {total === 0
                ? "Start anywhere. One row is enough for today."
                : `${total} row${total === 1 ? "" : "s"} written so far.`}
            </h1>
          </div>
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            {total === 0
              ? "Most people start with resentments. You don't have to finish a pass before moving to another."
              : "No target, no bar. This is just what's accumulated."}
          </p>
          {foreignRows && (
            <Note icon={Key}>
              Some rows were written on another device and can't be read here — the key stays
              where they were written. They're still safe, and still only yours.
            </Note>
          )}
          <div className="mt-1 flex flex-col gap-2.5">
            {PASS_KEYS.map((p) => {
              const Icon = PASSES[p].icon;
              const list = byPass[p];
              const empty = list.length === 0;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setScreen(empty ? { s: "row", pass: p } : { s: "list", pass: p })}
                  className="text-left"
                >
                  <Card className="transition-colors hover:border-primary/40">
                    <CardContent className="flex items-center gap-3 p-4">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" strokeWidth={2} />
                      </span>
                      <div className="flex-1">
                        <p className="text-[15px] font-semibold text-foreground">{PASSES[p].label}</p>
                        <p className="text-xs text-muted-foreground">
                          {empty
                            ? `Start with one ${PASSES[p].one}`
                            : `${list.length} written · last ${shortDate(list[list.length - 1].updated_at)}`}
                        </p>
                      </div>
                      {empty ? (
                        <Plus className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
                      )}
                    </CardContent>
                  </Card>
                </button>
              );
            })}
          </div>
          {total > 0 && (
            <Button variant="outline" size="lg" fullWidth className="mt-1" onClick={() => setScreen({ s: "review" })}>
              Read Through Everything
            </Button>
          )}
          {data.work?.status !== "ready" && data.work?.status !== "worked" && total > 0 && (
            <Button variant="ghost" fullWidth onClick={() => setScreen({ s: "ready" })}>
              I'm Ready to Read It to Someone
            </Button>
          )}
          <div className="mt-auto flex flex-col items-start gap-2.5 pt-5">
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-[13px] w-[13px]" strokeWidth={2} /> Encrypted on this device · no sponsor access
            </span>
            <button
              type="button"
              className="text-[13px] text-muted-foreground underline underline-offset-2"
              onClick={() => setConfirmingDelete(true)}
              disabled={total === 0}
            >
              Delete My Step 4 Writing
            </button>
          </div>
        </div>
        {confirmingDelete && deleteModal()}
      </div>
    );
  }

  /* ------------------------------ add / edit a row ------------------------------ */
  if (screen.s === "row") {
    const p = PASSES[screen.pass];
    const existing = screen.rowId ? rows.find((r) => r.id === screen.rowId) : undefined;
    const index = existing
      ? byPass[screen.pass].findIndex((r) => r.id === screen.rowId) + 1
      : byPass[screen.pass].length + 1;
    return (
      <RowEditor
        key={screen.rowId ?? `${screen.pass}-new`}
        p={p}
        index={index}
        initial={existing?.fields ?? []}
        editing={!!existing}
        firstInPass={!existing && byPass[screen.pass].length === 0}
        busy={busy}
        onBack={() => setScreen({ s: "list", pass: screen.pass })}
        onSave={(fields) => void saveRow(screen.pass, screen.rowId, fields)}
        onExit={() => setScreen({ s: "hub" })}
      />
    );
  }

  /* ------------------------------ pass list ------------------------------ */
  if (screen.s === "list") {
    const p = PASSES[screen.pass];
    const list = byPass[screen.pass];
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 4", { back: () => setScreen({ s: "hub" }) })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-3 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">{p.label}</p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              {list.length} written
            </h1>
          </div>
          <div className="flex flex-col gap-2">
            {list.map((r) => {
              const unfinished = r.fields ? r.fields.some((f, i) => !f?.trim() && i < p.fields.length) : false;
              return (
                <button key={r.id} type="button" className="text-left" onClick={() => setScreen({ s: "row", pass: screen.pass, rowId: r.id })}>
                  <Card className="transition-colors hover:border-primary/40">
                    <CardContent className="flex items-center gap-3 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground">
                          {r.fields?.[0]?.trim() || (r.fields ? "Untitled" : "Written on another device")}
                        </p>
                        {r.fields?.[1]?.trim() && (
                          <p className="truncate text-xs text-muted-foreground">{r.fields[1]}</p>
                        )}
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {shortDate(r.updated_at)} · {unfinished ? "unfinished" : "complete"}
                        </p>
                      </div>
                      <PenLine className="h-[15px] w-[15px] shrink-0 text-muted-foreground" strokeWidth={2} />
                    </CardContent>
                  </Card>
                </button>
              );
            })}
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Tap a row to keep working on it. Rows aren't deleted one by one — when you're done with
            the whole inventory you can delete all of it at once.
          </p>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth onClick={() => setScreen({ s: "row", pass: screen.pass })}>
              <Plus className="h-4 w-4" strokeWidth={2} /> Add Another {p.one.charAt(0).toUpperCase() + p.one.slice(1)}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setScreen({ s: "hub" })}>
              Back to Step 4
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------ read-through ------------------------------ */
  if (screen.s === "review") {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Read through", { back: () => setScreen({ s: "hub" }) })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Everything you've written
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              {total} row{total === 1 ? "" : "s"}, in the order you wrote them.
            </h1>
            <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
              This is the page you'll read from in Step 5, out loud, to someone you trust.
            </p>
          </div>
          {PASS_KEYS.map((pk) => {
            const p = PASSES[pk];
            const list = byPass[pk];
            return (
              <div key={pk} className="flex flex-col gap-2">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  {p.label} · {list.length}
                </p>
                {list.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nothing written yet.</p>
                )}
                {list.map((r) => (
                  <div key={r.id} className="rounded-lg border border-border bg-muted px-3.5 py-3">
                    {r.fields ? (
                      r.fields.map((v, i) =>
                        v?.trim() ? (
                          <p key={i} className={i === 0 ? "text-sm font-semibold text-foreground" : "mt-1 text-[13px] leading-relaxed text-foreground"}>
                            {i > 0 && <span className="text-muted-foreground">{p.fields[i]?.label}: </span>}
                            {v}
                          </p>
                        ) : null
                      )
                    ) : (
                      <p className="text-[13px] text-muted-foreground">Written on another device — can't be read here.</p>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
          <div className="mt-auto pt-4">
            <Button size="xl" fullWidth onClick={() => setScreen({ s: "hub" })}>
              Back to Step 4
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------ ready ------------------------------ */
  if (screen.s === "ready") {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 4", { back: () => setScreen({ s: "hub" }), saved: true })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Four · Ready
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">Ready to read it to someone.</h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">
            You've written {total} row{total === 1 ? "" : "s"} across the three passes. There's no
            finished — there's only ready enough to read aloud.
          </p>
          {sponsors.length > 0 && (
            <Card>
              <CardContent className="flex items-center gap-3 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <HeartHandshake className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{sponsors[0].target_email}</p>
                  <p className="text-xs text-muted-foreground">Your sponsor · Step 5 happens in person</p>
                </div>
              </CardContent>
            </Card>
          )}
          <Note>
            There's nothing to share here. Your sponsor can see that you've marked Step 4 ready —
            never a word of what you wrote. You read it to them yourself.
          </Note>
          <div className="flex items-center gap-3 rounded-lg border-2 border-primary bg-primary/[0.06] px-4 py-3.5">
            <CircleCheck className="h-5 w-5 text-primary" strokeWidth={2} />
            <div>
              <p className="text-sm font-medium text-foreground">Mark Step 4 ready</p>
              <p className="text-xs text-muted-foreground">You can keep adding rows after this.</p>
            </div>
          </div>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth disabled={busy} onClick={() => void markReady()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Let My Sponsor Know I'm Ready"}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setScreen({ s: "hub" })}>
              Not Yet
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------ close ------------------------------ */
  if (screen.s === "close") {
    return (
      <div className="flex min-h-dvh flex-col px-6 pb-8 pt-6">
        <div className="animate-fade-rise flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <LogoMark className="h-14 w-14 animate-scale-in" />
          <h1 className="text-2xl font-bold text-foreground">You've marked Step 4 ready.</h1>
          <p className="mx-auto max-w-[296px] text-[15px] leading-relaxed text-muted-foreground">
            That was the long one. Set a time with your sponsor and read it to them — that's where
            the weight starts to move. Your writing stays encrypted here until you delete it.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <Button size="xl" fullWidth onClick={() => navigate("/steps")}>Return to Steps</Button>
          <Button variant="ghost" fullWidth onClick={() => navigate("/checkin")}>
            Back to Today's Check-In
          </Button>
        </div>
      </div>
    );
  }

  /* ------------------------------ deleted ------------------------------ */
  return (
    <div className="flex min-h-dvh flex-col px-6 pb-8 pt-6">
      <div className="animate-fade-rise flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Check className="h-7 w-7" strokeWidth={2} />
        </span>
        <h1 className="text-[21px] font-bold text-foreground">Your Step 4 writing is gone.</h1>
        <p className="mx-auto max-w-[290px] text-[15px] leading-relaxed text-muted-foreground">
          Deleted for good — it isn't in a trash folder anywhere. Step 4 stays marked done
          {data.work?.status === "worked" ? ", and Step 5 is open" : ""}.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Button size="xl" fullWidth onClick={() => navigate("/steps")}>Return to Steps</Button>
      </div>
    </div>
  );

  function deleteModal() {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/45 p-3">
        <div className="flex w-full max-w-md flex-col gap-3 rounded-xl border border-border bg-card p-6 shadow-lg">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-status-red-bg text-status-red-text">
            <Trash2 className="h-5 w-5" strokeWidth={2} />
          </span>
          <h2 className="text-lg font-bold text-foreground">Delete your Step 4 writing?</h2>
          <p className="text-sm leading-relaxed text-foreground">
            This permanently deletes everything you wrote in Step 4 — all {total} row
            {total === 1 ? "" : "s"}, all three passes. You'll keep your progress and can continue
            to Step 5. This can't be undone.
          </p>
          {data?.work?.status !== "worked" && (
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              If you haven't read it to your sponsor yet, it's worth waiting.
            </p>
          )}
          <div className="mt-1 flex flex-col gap-2">
            <Button variant="destructive" size="lg" fullWidth disabled={busy} onClick={() => void deleteEverything()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Delete Everything"}
            </Button>
            <Button variant="ghost" size="lg" fullWidth onClick={() => setConfirmingDelete(false)}>
              Keep My Writing
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

/** The one reusable add-a-row screen — identical for all three passes. */
function RowEditor({
  p,
  index,
  initial,
  editing,
  firstInPass,
  busy,
  onBack,
  onSave,
  onExit,
}: {
  p: (typeof PASSES)[Step4Pass];
  index: number;
  initial: string[];
  editing: boolean;
  firstInPass: boolean;
  busy: boolean;
  onBack: () => void;
  onSave: (fields: string[]) => void;
  onExit: () => void;
}) {
  const [fields, setFields] = useState<string[]>(
    p.fields.map((_, i) => initial[i] ?? "")
  );
  const singular = p.label.replace(/s$/, "");
  return (
    <div className="flex min-h-dvh flex-col pb-8">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <span className="flex-1 text-sm font-semibold text-foreground">{p.label}</span>
        <span className="pr-2"><EncryptedTag /></span>
      </div>
      <div className="animate-fade-rise flex flex-1 flex-col gap-3.5 px-6 pt-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            {p.label} · {editing ? "editing" : "new"}
          </p>
          <h1 className="mt-1 text-xl font-semibold text-foreground">
            {singular} {index}
          </h1>
        </div>
        {firstInPass && (
          <Note icon={Lock}>First one in this pass. It only has to be one.</Note>
        )}
        {p.fields.map((f, i) => (
          <div key={f.label}>
            <label className="mb-0.5 block text-[13px] font-semibold text-foreground">{f.label}</label>
            <p className="mb-1.5 text-xs leading-snug text-muted-foreground">{f.help}</p>
            {f.kind === "input" ? (
              <Input
                placeholder="In your own words…"
                value={fields[i]}
                onChange={(e) => setFields(fields.map((v, j) => (j === i ? e.target.value : v)))}
              />
            ) : (
              <Textarea
                rows={3}
                placeholder="In your own words…"
                value={fields[i]}
                onChange={(e) => setFields(fields.map((v, j) => (j === i ? e.target.value : v)))}
              />
            )}
          </div>
        ))}
        <p className="text-xs leading-relaxed text-muted-foreground">
          {editing
            ? "You can fix a row while you're still writing this step."
            : "Leave a field blank and come back to it."}
        </p>
        <div className="mt-auto flex flex-col gap-2 pt-4">
          <Button
            size="xl"
            fullWidth
            disabled={busy || fields.every((v) => !v.trim())}
            onClick={() => onSave(fields)}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            ) : editing ? (
              "Update This Row"
            ) : (
              `Save This ${p.one.charAt(0).toUpperCase() + p.one.slice(1)}`
            )}
          </Button>
          <Button variant="ghost" fullWidth onClick={onExit}>
            Save &amp; Exit
          </Button>
        </div>
      </div>
    </div>
  );
}
